import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiUserPlus, FiRefreshCw, FiMessageCircle, FiEdit, FiEye, FiUsers, FiShield, FiBookOpen, FiToggleLeft, FiToggleRight, FiPlus, FiX } from 'react-icons/fi';

const Header = ({ title }) => (
  <div>
    <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
  </div>
);

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    phone: '',
    role: '',
    email: '',
    status: '',
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'u_id',
    direction: 'asc'
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    f_name: '',
    email: '',
    phonenumber: '',
    role: 'teacher',
    password: '',
    status: 'active'
  });

  // Count variables
  const totalUsers = users.length;
  const adminCount = users.filter(user => user.role === 'admin').length;
  const teacherCount = users.filter(user => user.role === 'teacher').length;
  const counsellorCount = users.filter(user => user.role === 'counsellor').length;
  const activeCount = users.filter(user => user.status === 'active').length;
  const inactiveCount = users.filter(user => user.status === 'inactive').length;

  // Calculate percentages
  const adminPercentage = totalUsers > 0 ? Math.round((adminCount / totalUsers) * 100) : 0;
  const teacherPercentage = totalUsers > 0 ? Math.round((teacherCount / totalUsers) * 100) : 0;
  const counsellorPercentage = totalUsers > 0 ? Math.round((counsellorCount / totalUsers) * 100) : 0;
  const activePercentage = totalUsers > 0 ? Math.round((activeCount / totalUsers) * 100) : 0;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/users/showUsers');
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch users');
      setLoading(false);
      console.error('Error fetching users:', err);
    }
  };

  // Filter the users based on search criteria
  const filteredUsers = users.filter(user => {
    const nameMatch = user.f_name.toLowerCase().includes(filters.name.toLowerCase());
    const phoneMatch = user.phonenumber.includes(filters.phone);
    const roleMatch = !filters.role || user.role === filters.role.toLowerCase();
    const emailMatch = !filters.email || user.email.toLowerCase().includes(filters.email.toLowerCase());
    const statusMatch = !filters.status || user.status === filters.status.toLowerCase();
    
    return nameMatch && phoneMatch && roleMatch && emailMatch && statusMatch;
  });

  // Sorting function
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort users based on current sort configuration
  const sortedUsers = React.useMemo(() => {
    const sortableUsers = [...filteredUsers];
    if (sortConfig.key) {
      sortableUsers.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [filteredUsers, sortConfig]);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };

  // Get unique roles for filter
  const uniqueRoles = ['admin', 'teacher', 'counsellor'];
  const statusOptions = ['active', 'inactive'];

  // Get initial letter of name for avatar
  const getInitial = (name) => {
    return name?.charAt(0)?.toUpperCase() || 'U';
  };

  // Format date from ISO to DD/MM/YYYY
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Open create user modal
  const handleCreateClick = () => {
    setFormData({
      f_name: '',
      email: '',
      phonenumber: '',
      role: 'teacher',
      password: '',
      status: 'active'
    });
    setShowCreateModal(true);
  };

  // Create new user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('https://apissc.sainikschoolcadet.com/api/users/createUser', formData);
      setUsers([...users, response.data.user]);
      setShowCreateModal(false);
      // Show success message
      alert('User created successfully');
    } catch (err) {
      alert('Failed to create user: ' + (err.response?.data?.error || err.message));
      console.error('Error creating user:', err);
    }
  };

  // Open edit user modal
  const handleEditUser = (user) => {
    setCurrentUser(user);
    setFormData({
      f_name: user.f_name,
      email: user.email,
      phonenumber: user.phonenumber,
      role: user.role,
      status: user.status || 'active', // Default to active if status doesn't exist
      password: '' // Leave password empty
    });
    setShowEditModal(true);
  };

  // Update user
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...formData };
      // Only include password if it was provided
      if (!updateData.password) delete updateData.password;
      
      const response = await axios.put(`https://apissc.sainikschoolcadet.com/api/users/updateUser/${currentUser.u_id}`, updateData);
      
      // Update users array with the updated user
      setUsers(users.map(user => user.u_id === currentUser.u_id ? response.data.user : user));
      setShowEditModal(false);
      alert('User updated successfully');
    } catch (err) {
      alert('Failed to update user: ' + (err.response?.data?.error || err.message));
      console.error('Error updating user:', err);
    }
  };

  // Toggle user status
  const handleToggleStatus = async (user) => {
    try {
      const response = await axios.patch(`https://apissc.sainikschoolcadet.com/api/users/toggleStatus/${user.u_id}`);
      
      // Update users array with the updated status
      setUsers(users.map(u => u.u_id === user.u_id ? response.data.user : u));
      
      // Show success message
      alert(`User status changed to ${response.data.user.status}`);
    } catch (err) {
      alert('Failed to toggle status: ' + (err.response?.data?.error || err.message));
      console.error('Error toggling status:', err);
    }
  };

  // Get role color
  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'teacher':
        return 'bg-green-100 text-green-800';
      case 'counsellor':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status color and style
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
            Active
          </span>
        );
      case 'inactive':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
            Inactive
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  if (loading) {
    return <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl shadow-sm flex justify-center items-center h-64">Loading...</div>;
  }

  if (error) {
    return <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl shadow-sm flex justify-center items-center h-64 text-red-500">{error}</div>;
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-6 bg-white rounded-3xl">
      {/* Header with title and action buttons */}
      <div className="flex justify-between items-center mb-6">
        <Header title="Users" />
        <div className="flex gap-2">
          <button 
            onClick={handleCreateClick}
            className="px-4 py-2 bg-blue-600 text-white rounded flex items-center"
          >
            <FiPlus className="mr-1" />
            Add User
          </button>
          <button 
            onClick={fetchUsers}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded flex items-center"
          >
            <FiRefreshCw className="mr-1" />
            Refresh
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-full mr-4">
              <FiUsers className="text-blue-500" size={20} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Users</p>
              <p className="text-2xl font-semibold">{totalUsers}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-full mr-4">
              <FiShield className="text-blue-500" size={20} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Admin</p>
              <p className="text-2xl font-semibold">{adminCount}</p>
              <p className="text-xs text-gray-500">
                {adminPercentage}% of total
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-full mr-4">
              <FiBookOpen className="text-green-500" size={20} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Teacher</p>
              <p className="text-2xl font-semibold">{teacherCount}</p>
              <p className="text-xs text-gray-500">
                {teacherPercentage}% of total
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-full mr-4">
              <FiMessageCircle className="text-purple-500" size={20} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Counsellor</p>
              <p className="text-2xl font-semibold">{counsellorCount}</p>
              <p className="text-xs text-gray-500">
                {counsellorPercentage}% of total
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-full mr-4">
              <FiToggleRight className="text-green-500" size={20} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Active</p>
              <p className="text-2xl font-semibold">{activeCount}</p>
              <p className="text-xs text-gray-500">
                {activePercentage}% of total
              </p>
            </div>
          </div>
        </div>

        <div className="bg-red-50 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="bg-red-100 p-3 rounded-full mr-4">
              <FiToggleLeft className="text-red-500" size={20} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Inactive</p>
              <p className="text-2xl font-semibold">{inactiveCount}</p>
              <p className="text-xs text-gray-500">
                {100 - activePercentage}% of total
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                S NO <span className="cursor-pointer inline-block ml-1" onClick={() => requestSort('u_id')}>⇅</span>
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                NAME <span className="cursor-pointer inline-block ml-1" onClick={() => requestSort('f_name')}>⇅</span>
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                PHONE NUMBER <span className="cursor-pointer inline-block ml-1" onClick={() => requestSort('phonenumber')}>⇅</span>
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ROLE <span className="cursor-pointer inline-block ml-1" onClick={() => requestSort('role')}>⇅</span>
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                STATUS <span className="cursor-pointer inline-block ml-1" onClick={() => requestSort('status')}>⇅</span>
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                EMAIL <span className="cursor-pointer inline-block ml-1" onClick={() => requestSort('email')}>⇅</span>
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CREATED DATE <span className="cursor-pointer inline-block ml-1" onClick={() => requestSort('created_at')}>⇅</span>
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ACTIONS
              </th>
            </tr>
            <tr>
              {/* Filter Inputs */}
              <td className="px-3 py-2 border-t"></td>
              <td className="px-3 py-2 border-t">
                <input
                  type="text"
                  placeholder="Search Name"
                  className="w-full p-2 text-sm border rounded"
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                />
              </td>
              <td className="px-3 py-2 border-t">
                <input
                  type="text"
                  placeholder="Search Phone"
                  className="w-full p-2 text-sm border rounded"
                  value={filters.phone}
                  onChange={(e) => handleFilterChange('phone', e.target.value)}
                />
              </td>
              <td className="px-3 py-2 border-t">
                <select
                  className="w-full p-2 text-sm border rounded"
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                >
                  <option value="">All</option>
                  {uniqueRoles.map(role => (
                    <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                  ))}
                </select>
              </td>
              <td className="px-3 py-2 border-t">
                <select
                  className="w-full p-2 text-sm border rounded"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">All</option>
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                  ))}
                </select>
              </td>
              <td className="px-3 py-2 border-t">
                <input
                  type="text"
                  placeholder="Search Email"
                  className="w-full p-2 text-sm border rounded"
                  value={filters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                />
              </td>
              <td className="px-3 py-2 border-t"></td>
              <td className="px-3 py-2 border-t"></td>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedUsers.map((user, index) => (
              <tr key={user.u_id} className="hover:bg-gray-50">
                <td className="px-3 py-4 whitespace-nowrap text-sm">
                  {index + 1}
                </td>
                <td className="px-3 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mr-3">
                      {getInitial(user.f_name)}
                    </div>
                    <div className="text-sm">{user.f_name}</div>
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{user.phonenumber}</td>
                <td className="px-3 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleColor(user.role)}`}>
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </span>
                </td>
                <td className="px-3 py-4 whitespace-nowrap">
                  {getStatusBadge(user.status)}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.created_at)}</td>
                <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-3">
                    <button 
                      className="text-blue-600 hover:text-blue-900" 
                      onClick={() => handleEditUser(user)} 
                      title="Edit User"
                    >
                      <FiEdit size={18} />
                    </button>
                    <button 
                      className={user.status === 'active' ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"} 
                      onClick={() => handleToggleStatus(user)}
                      title={user.status === 'active' ? "Mark as Inactive" : "Mark as Active"}
                    >
                      {user.status === 'active' ? <FiToggleRight size={18} /> : <FiToggleLeft size={18} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sortedUsers.length === 0 && (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                  {filteredUsers.length === 0 ? 'No users found' : 'No users match your search criteria'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Create New User</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-500">
                <FiX size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Name</label>
                <input
                  type="text"
                  name="f_name"
                  value={formData.f_name}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phonenumber"
                  value={formData.phonenumber}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                >
                  {uniqueRoles.map(role => (
                    <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && currentUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Edit User: {currentUser.f_name}</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500">
                <FiX size={20} />
              </button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Name</label>
                <input
                  type="text"
                  name="f_name"
                  value={formData.f_name}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phonenumber"
                  value={formData.phonenumber}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                >
                  {uniqueRoles.map(role => (
                    <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Password <span className="text-gray-500 text-xs">(Leave blank to keep current)</span>
                </label>
                <input
                 
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;